import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API, clearCsrfToken, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

function errResult(e) {
  return {
    ok: false,
    error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
    rateLimited: e.response?.status === 429,
  };
}

export function AuthProvider({ children }) {
  // status: "checking" | "authed" | "anon"
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data.user);
      setStatus("authed");
    } catch {
      // A stale access token can still be revived by the refresh cookie.
      try {
        const { data } = await API.post("/auth/refresh");
        setUser(data.user);
        setStatus("authed");
      } catch {
        setUser(null);
        setStatus("anon");
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const onExpired = () => {
      setUser(null);
      setStatus("anon");
    };
    window.addEventListener("virtue:session-expired", onExpired);
    return () => window.removeEventListener("virtue:session-expired", onExpired);
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await API.post("/auth/login", { email, password });
      // The CSRF token in the body is picked up by the API response interceptor.
      // MFA accounts get a challenge instead of a session: nothing is signed in
      // until the second step succeeds.
      if (data.mfa_required) return { ok: true, mfaRequired: true, challenge: data.challenge };
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const verifyMfa = useCallback(async (challenge, code, rememberDevice = false) => {
    try {
      const { data } = await API.post("/auth/mfa/verify", {
        challenge,
        code,
        remember_device: rememberDevice,
      });
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const { data } = await API.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(data.user);
      return { ok: true, revoked: data.other_sessions_revoked };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await API.post("/auth/logout");
    } catch {
      /* force local sign-out regardless */
    }
    clearCsrfToken();
    setUser(null);
    setStatus("anon");
  }, []);

  const logoutEverywhere = useCallback(async () => {
    try {
      await API.post("/auth/logout-all");
    } catch {
      /* force local sign-out regardless */
    }
    clearCsrfToken();
    setUser(null);
    setStatus("anon");
  }, []);

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        isAdmin: user?.role === "admin",
        mustChangePassword: Boolean(user?.must_change_password),
        mustEnrollMfa: Boolean(user?.mfa_required) && !user?.mfa_enabled,
        login,
        verifyMfa,
        logout,
        logoutEverywhere,
        changePassword,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
